import { Badge, Box, Button, Code, Group, SegmentedControl, Text, Tooltip } from '@mantine/core';
import {
  IconCapture,
  IconChevronDown,
  IconChevronRight,
  IconChevronsDown,
  IconChevronsUp,
  IconDownload,
  IconFileTypeTxt,
  IconInbox,
  IconJson,
  IconPaperclip,
  IconTrash,
} from '@tabler/icons-react';
import i18next from 'i18next';
import { DataTable } from 'mantine-datatable';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { File, Form, FormView, HttpUpdateMessage, LocationInfo, Message } from '@/api';
import { getCachedDecryptedMessage } from '../../lib/nostr-decrypt';
import { absoluteTime, relativeTime } from '../../lib/relative-time';
import type { CommonTableProps } from '../../lib/table';
import { useRustyState } from '../../state';
import { EmptyState } from '../Common/EmptyState';
import { SpamScoreChip } from './dashboard/SpamScoreChip';
import { NostrDecryptButton } from './NostrDecryptButton';
import classes from './Table.module.css';

const SPAM_FILTER_STORAGE_KEY = 'formshive.messages.spamFilter';

function readSpamFilterFromStorage(): number {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 0;
  }
  const raw = window.localStorage.getItem(SPAM_FILTER_STORAGE_KEY);
  if (raw === null) {
    return 0;
  }
  const num = parseInt(raw, 10);
  return Number.isFinite(num) && num >= 0 && num <= 2 ? num : 0;
}

export function MessagesTable(
  props: CommonTableProps<
    {
      msg: Message;
      form: Form | undefined;
      files: File[];
    },
    HttpUpdateMessage
  >
) {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const [records, setRecords] = useState<Message[] | []>([]);
  const [forms, setForms] = useState<Form[] | []>([]);
  const [files, setFiles] = useState<File[] | []>([]);
  const [viewData, setViewData] = useState<Map<string, FormView | null>>(new Map());
  const [loadingViews, setLoadingViews] = useState<Set<string>>(new Set());
  const [decryptedMessages, setDecryptedMessages] = useState<Map<string, Record<string, unknown>>>(
    new Map()
  );
  const [expandedRecordIds, setExpandedRecordIds] = useState<unknown[]>([]);
  // Refs mirror state so the fetch trigger can dedupe without re-running on each render.
  const viewDataRef = useRef(viewData);
  const loadingViewsRef = useRef(loadingViews);
  const page = useRef(1);
  const spamFilter = useRef<number>(readSpamFilterFromStorage());
  const [spamFilterValue, setSpamFilterValue] = useState<number>(spamFilter.current);

  useEffect(() => {
    viewDataRef.current = viewData;
  }, [viewData]);

  useEffect(() => {
    loadingViewsRef.current = loadingViews;
  }, [loadingViews]);

  const loadMessageView = useCallback(async (messageId: string) => {
    if (viewDataRef.current.has(messageId) || loadingViewsRef.current.has(messageId)) {
      return; // Already loaded or loading
    }

    setLoadingViews((prev) => {
      const next = new Set(prev).add(messageId);
      loadingViewsRef.current = next;
      return next;
    });

    try {
      const view = await useRustyState.getState().api.getMessageView(messageId);
      setViewData((prev) => {
        const next = new Map(prev).set(messageId, view);
        viewDataRef.current = next;
        return next;
      });
    } catch (error) {
      console.error('Failed to fetch message view:', error);
      setViewData((prev) => {
        const next = new Map(prev).set(messageId, null);
        viewDataRef.current = next;
        return next;
      });
    } finally {
      setLoadingViews((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        loadingViewsRef.current = next;
        return next;
      });
    }
  }, []);

  const handleExpandedRecordIdsChange = useCallback(
    (recordIds: unknown[]) => {
      setExpandedRecordIds(recordIds);
      // Fire the view fetch only for newly expanded rows; loadMessageView is idempotent.
      for (const id of recordIds) {
        if (typeof id === 'string') {
          loadMessageView(id);
        }
      }
    },
    [loadMessageView]
  );

  const allExpanded = records.length > 0 && expandedRecordIds.length === records.length;

  const toggleExpandAll = useCallback(() => {
    if (allExpanded) {
      setExpandedRecordIds([]);
      return;
    }
    handleExpandedRecordIdsChange(records.map((r) => r.id));
  }, [allExpanded, records, handleExpandedRecordIdsChange]);

  const loadRecords = async () => {
    setIsBusy(true);
    try {
      // Build the params for the message fetch
      const messageParams =
        spamFilter.current === 0
          ? { nextPage: page.current }
          : { nextPage: page.current, is_spam: spamFilter.current === 1 };

      // Run forms fetch and message fetch in parallel on first page
      const [data] =
        page.current === 1
          ? await Promise.all([
              props.onChange(messageParams),
              useRustyState.getState().getAndSetForms(),
            ])
          : [await props.onChange(messageParams)];
      const msgs: Message[] = [];
      const newForms: Form[] = [];
      let newFiles: File[] = [];
      data.forEach((d) => {
        if (d.msg) {
          msgs.push(d.msg);
        }
        if (d.form && !newForms.includes(d.form)) {
          newForms.push(d.form);
        }
        if (d.files) {
          newFiles = [...newFiles, ...d.files];
        }
      });
      setRecords(msgs);
      setForms(newForms);
      setFiles(newFiles);
      setExpandedRecordIds([]);
    } catch (e) {
      alert(e);
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };

  useEffect(() => {
    const doIt = async () => {
      await loadRecords();
    };
    doIt();
  }, []);

  // Check cache for decrypted nostr messages
  useEffect(() => {
    const loadDecryptedFromCache = async () => {
      const nostrMessages = records.filter((m) => m.encryption_mode === 'nostr');
      for (const msg of nostrMessages) {
        if (!decryptedMessages.has(msg.id)) {
          const cached = await getCachedDecryptedMessage(msg.id);
          if (cached) {
            setDecryptedMessages((prev) => new Map(prev).set(msg.id, cached));
          }
        }
      }
    };
    loadDecryptedFromCache();
  }, [records]);

  // Callback when a message is decrypted via NostrDecryptButton
  const onMessageDecrypted = (messageId: string, data: Record<string, unknown>) => {
    setDecryptedMessages((prev) => new Map(prev).set(messageId, data));
  };

  const toggleSpam = async (message: Message) => {
    setIsBusy(true);
    if (!props.updateCb) {
      alert(t('messageTable.couldNotToggleSpam'));
      return;
    }

    const customNotification = {
      onSuccess: {
        title: t('messageTable.messageUpdated'),
        message: `Message ${message.is_spam ? t('messageTable.markedAsHam') : t('messageTable.markedAsSpam')}`,
      },
    };

    await props.updateCb(message.id, { is_spam: !message.is_spam }, customNotification);
    setRecords((prev) =>
      prev.map((m) => {
        if (m.id === message.id) {
          return { ...m, is_spam: !m.is_spam };
        }
        return m;
      })
    );
    setIsBusy(false);
  };

  const deleteCb = async (id: string) => {
    setIsBusy(true);
    await props.deleteCb(id);
    setRecords((prev) => prev.filter((m) => m.id !== id));
    setIsBusy(false);
  };

  const buildPreviewString = (data: Record<string, unknown>): string => {
    let out = '';
    Object.keys(data).forEach((key) => {
      out += `${key}: ${data[key]}, `;
    });
    return out.length > 100 ? `${out.substring(0, 100)}…` : out.replace(/,\s*$/, '');
  };

  const messagePreview = (message: Message) => {
    // For Nostr-encrypted messages, check if we have decrypted it
    if (message.encryption_mode === 'nostr') {
      const decrypted = decryptedMessages.get(message.id);
      if (decrypted) {
        return (
          <Group gap="xs" wrap="nowrap">
            <Badge color="teal" size="xs">
              {t('messageTable.decrypted')}
            </Badge>
            <Text size="sm" lineClamp={1}>
              {buildPreviewString(decrypted)}
            </Text>
          </Group>
        );
      }
      // Not yet decrypted - show placeholder
      return (
        <Badge color="green" leftSection="E2E">
          {t('messageTable.encryptedNostr')}
        </Badge>
      );
    }

    // Server-encrypted messages are decrypted server-side before being sent to the client
    // so we can display them normally, but we show they're encrypted at rest
    try {
      const msgData = JSON.parse(message.data);
      const preview = buildPreviewString(msgData);

      if (message.encryption_mode === 'server') {
        return (
          <Group gap="xs" wrap="nowrap">
            <Badge color="blue" size="xs">
              Encrypted
            </Badge>
            <Text size="sm" lineClamp={1}>
              {preview}
            </Text>
          </Group>
        );
      }

      return (
        <Text size="sm" lineClamp={1}>
          {preview}
        </Text>
      );
    } catch {
      // If we can't parse the data, it might be encrypted
      if (message.encryption_mode === 'server') {
        return <Badge color="blue">{t('messageTable.encryptedServer')}</Badge>;
      }
      return (
        <Text size="sm" c="dimmed">
          Unable to parse message data
        </Text>
      );
    }
  };

  const filesPreview = (message_id: string) => {
    const msgFiles = files.filter((f) => f.message_id === message_id);
    const count = msgFiles.length;
    return (
      <Tooltip label={t('messageTable.filesCount', { count })}>
        <IconPaperclip
          size={20}
          style={{
            color:
              count > 0 ? 'var(--mantine-color-primary-filled)' : 'var(--mantine-color-gray-5)',
            opacity: count > 0 ? 1 : 0.3,
          }}
        />
      </Tooltip>
    );
  };

  const downloadFile = async (id: string) => {
    const signedUrl = await useRustyState.getState().downloadFile(id);
    if (signedUrl) {
      const link = document.createElement('a');
      link.href = signedUrl;
      link.setAttribute('download', 'true'); // Optional: Suggests to the browser to download rather than navigate.
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const messageHasFiles = (message_id: string) =>
    files.filter((f) => f.message_id === message_id).length > 0;

  const filesDownload = (message_id: string) => {
    const msgFiles = files.filter((f) => f.message_id === message_id);
    return (
      <Group>
        {msgFiles.map((f) => (
          <Button
            leftSection={<IconDownload style={{ width: '70%', height: '70%' }} />}
            key={`files-to-download-${message_id}-${f.id}`}
            color="gray"
            size="compact-sm"
            onClick={() => downloadFile(f.id)}
          >
            Download {f.filename} ({f.size} bytes)
          </Button>
        ))}
      </Group>
    );
  };

  const formPreview = (form_id: string) => {
    const form = forms.find((f) => f.id === form_id);
    if (!form) {
      return (
        <Text size="sm" c="dimmed">
          -
        </Text>
      );
    }
    return (
      <Badge variant="light" color="gray" size="md" radius="sm">
        {form.title}
      </Badge>
    );
  };

  const dateTime = (value: string | Date) => {
    const locale = (i18next.language || 'en').split('-')[0];
    return (
      <Tooltip inline label={absoluteTime(value, locale)}>
        <Text size="sm">{relativeTime(value, locale)}</Text>
      </Tooltip>
    );
  };

  const formatLocationDetails = (location?: LocationInfo) => {
    if (!location) {
      return (
        <Text size="xs" c="dimmed">
          Unknown
        </Text>
      );
    }

    const locationParts = [
      location.city,
      location.region_name,
      location.country_name || location.country_code,
    ].filter(Boolean);

    return (
      <Box>
        <Text size="xs" c="dimmed">
          Location:{' '}
          <Text span fw={500}>
            {locationParts.join(', ') || 'Unknown'}
          </Text>
        </Text>
        {location.timezone && (
          <Text size="xs" c="dimmed">
            Timezone:{' '}
            <Text span fw={500}>
              {location.timezone}
            </Text>
          </Text>
        )}
        {location.latitude && location.longitude && (
          <Text size="xs" c="dimmed">
            Coordinates:{' '}
            <Text span fw={500}>
              {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
            </Text>
          </Text>
        )}
      </Box>
    );
  };

  const columns = [
    {
      accessor: 'expand',
      title: '',
      width: 36,
      render: (row: Message) => (
        <Box c="dimmed" style={{ display: 'flex', alignItems: 'center' }}>
          {expandedRecordIds.includes(row.id) ? (
            <IconChevronDown size={16} />
          ) : (
            <IconChevronRight size={16} />
          )}
        </Box>
      ),
    },
    {
      accessor: 'Content',
      title: t('messageTable.content'),
      render: (row: Message) => messagePreview(row),
    },
    {
      accessor: 'Form',
      title: t('messageTable.form'),
      render: (row: Message) => formPreview(row.form_id),
    },
    {
      accessor: 'Files',
      title: t('messageTable.files'),
      width: 70,
      textAlign: 'center' as const,
      render: (row: Message) => filesPreview(row.id),
    },
    {
      accessor: 'is_spam',
      title: t('messageTable.spam'),
      width: 100,
      render: (row: Message) => <SpamScoreChip isSpam={row.is_spam} score={row.spam_score} />,
    },
    {
      accessor: 'created_at',
      title: t('messageTable.createdAt'),
      textAlign: 'right' as const,
      width: 130,
      render: (row: Message) => dateTime(row.created_at),
    },
  ];

  const formatTimeDifference = (viewTime: string, messageTime: string): string => {
    const viewDate = new Date(viewTime);
    const messageDate = new Date(messageTime);
    const diffMs = messageDate.getTime() - viewDate.getTime();

    if (diffMs < 0) {
      return 'Message submitted before view (unusual)';
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} after view`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} after view`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} after view`;
    } else {
      return `${diffSeconds} second${diffSeconds > 1 ? 's' : ''} after view`;
    }
  };

  const downloadMessage = (message: Message, format: 'json' | 'txt') => {
    const msgData = JSON.parse(message.data);
    let data = '';
    if (format === 'json') {
      data = JSON.stringify(msgData, null, 2);
    } else {
      Object.keys(msgData).map((key) => (data += `${key}: ${msgData[key]}\n`));
    }

    const element = document.createElement('a');
    const file = new Blob([data], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `formshive-message-${message.id}.${format}`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  const expandedRowHtml = (
    row: Message,
    loading: boolean,
    toggleSpamCb: (record: Message) => void,
    deleteCbRow: (id: string) => void
  ) => {
    // For Nostr-encrypted messages, show decrypt button instead of trying to parse
    if (row.encryption_mode === 'nostr') {
      return (
        <Box>
          <NostrDecryptButton
            message={row}
            onDecrypted={(data) => onMessageDecrypted(row.id, data)}
          />

          {messageHasFiles(row.id) && (
            <Box mt="md">
              <Text size="sm">Files:</Text>
              {filesDownload(row.id)}
            </Box>
          )}

          {/* View Analytics Section */}
          <Box mt="md">
            <Text size="sm" fw={500} mb="xs">
              View Analytics:
            </Text>
            {(() => {
              const view = viewData.get(row.id);
              const isLoading = loadingViews.has(row.id);

              if (view === null) {
                return (
                  <Text size="sm" c="dimmed">
                    No associated form view found
                  </Text>
                );
              }

              if (view === undefined || isLoading) {
                return (
                  <Text size="sm" c="dimmed">
                    Loading view data...
                  </Text>
                );
              }

              return (
                <Box>
                  <Group gap="md" mb="xs">
                    <Badge color="blue" variant="light">
                      {view.device_type || 'Unknown'} Device
                    </Badge>
                    <Badge color="green" variant="light">
                      {view.browser_name || 'Unknown'} Browser
                    </Badge>
                    {view.location?.country_code && (
                      <Badge color="orange" variant="light">
                        {view.location.country_code}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    Form submitted{' '}
                    <Text span fw={500}>
                      {formatTimeDifference(view.created_at, row.created_at)}
                    </Text>
                  </Text>
                  {view.traffic_source && (
                    <Text size="xs" c="dimmed">
                      Traffic source:{' '}
                      <Text span fw={500}>
                        {view.traffic_source}
                      </Text>
                    </Text>
                  )}
                  <Box mb="sm" mt="sm">
                    <Text size="xs" fw={500} c="orange">
                      Location:
                    </Text>
                    {formatLocationDetails(row.location)}
                  </Box>
                </Box>
              );
            })()}
          </Box>

          <Group mt="md">
            <Button
              onClick={() => toggleSpamCb(row)}
              loading={loading}
              color={row.is_spam ? 'teal' : 'red'}
              mb="xs"
              size="compact-sm"
              leftSection={<IconCapture style={{ width: '70%', height: '70%' }} />}
            >
              {row.is_spam ? t('messageTable.markAsHam') : t('messageTable.markAsSpam')}
            </Button>
            <Button
              onClick={() => deleteCbRow(row.id)}
              loading={loading}
              color="red"
              mb="xs"
              size="compact-sm"
              leftSection={<IconTrash style={{ width: '70%', height: '70%' }} />}
            >
              {t('messageTable.delete')}
            </Button>
          </Group>
        </Box>
      );
    }

    // For non-encrypted or server-encrypted messages, show normal content
    const data = JSON.parse(row.data);
    return (
      <Box>
        <Code block>
          {Object.keys(data).map((key) => (
            <Box key={`data-${key}`}>
              <Text>
                {key}: {Array.isArray(data[key]) ? data[key].join(', ') : data[key]}
              </Text>
            </Box>
          ))}
        </Code>

        {messageHasFiles(row.id) && (
          <Box mt="md">
            <Text size="sm">Files:</Text>
            {filesDownload(row.id)}
          </Box>
        )}

        {/* View Analytics Section */}
        <Box mt="md">
          <Text size="sm" fw={500} mb="xs">
            View Analytics:
          </Text>
          {(() => {
            const view = viewData.get(row.id);
            const isLoading = loadingViews.has(row.id);

            if (view === null) {
              return (
                <Text size="sm" c="dimmed">
                  No associated form view found
                </Text>
              );
            }

            if (view === undefined || isLoading) {
              return (
                <Text size="sm" c="dimmed">
                  Loading view data...
                </Text>
              );
            }

            return (
              <Box>
                <Group gap="md" mb="xs">
                  <Badge color="blue" variant="light">
                    {view.device_type || 'Unknown'} Device
                  </Badge>
                  <Badge color="green" variant="light">
                    {view.browser_name || 'Unknown'} Browser
                  </Badge>
                  {view.location?.country_code && (
                    <Badge color="orange" variant="light">
                      {view.location.country_code}
                    </Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  Form submitted{' '}
                  <Text span fw={500}>
                    {formatTimeDifference(view.created_at, row.created_at)}
                  </Text>
                </Text>
                {view.traffic_source && (
                  <Text size="xs" c="dimmed">
                    Traffic source:{' '}
                    <Text span fw={500}>
                      {view.traffic_source}
                    </Text>
                  </Text>
                )}
                <Box mb="sm" mt="sm">
                  <Text size="xs" fw={500} c="orange">
                    Location:
                  </Text>
                  {formatLocationDetails(row.location)}
                </Box>
              </Box>
            );
          })()}
        </Box>

        <Group mt="md">
          <Button
            onClick={() => toggleSpamCb(row)}
            loading={loading}
            color={row.is_spam ? 'teal' : 'red'}
            mb="xs"
            size="compact-sm"
            leftSection={<IconCapture style={{ width: '70%', height: '70%' }} />}
          >
            {row.is_spam ? t('messageTable.markAsHam') : t('messageTable.markAsSpam')}
          </Button>
          <Button
            onClick={() => deleteCbRow(row.id)}
            loading={loading}
            color="red"
            mb="xs"
            size="compact-sm"
            leftSection={<IconTrash style={{ width: '70%', height: '70%' }} />}
          >
            {t('messageTable.delete')}
          </Button>
          <Button
            onClick={() => downloadMessage(row, 'json')}
            loading={loading}
            variant="subtle"
            color="gray"
            mb="xs"
            size="compact-sm"
            leftSection={<IconJson size={16} />}
          >
            {t('messageTable.downloadJson')}
          </Button>
          <Button
            onClick={() => downloadMessage(row, 'txt')}
            loading={loading}
            variant="subtle"
            color="gray"
            mb="xs"
            size="compact-sm"
            leftSection={<IconFileTypeTxt size={16} />}
          >
            {t('messageTable.downloadTxt')}
          </Button>
        </Group>
      </Box>
    );
  };

  // Handle perPage change triggered from parent
  useEffect(() => {
    if (props.perPage) {
      page.current = 1;
      loadRecords();
    }
  }, [props.perPage]);

  const handleSpamFilterChange = (value: string) => {
    const numVal = Number(value);
    spamFilter.current = numVal;
    setSpamFilterValue(numVal);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(SPAM_FILTER_STORAGE_KEY, String(numVal));
      }
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
    page.current = 1;
    loadRecords();
  };

  return (
    <Box>
      <Group justify="space-between" align="center" px="md" py="sm">
        <SegmentedControl
          data={[
            { value: '0', label: t('messageTable.filterAll') },
            { value: '2', label: t('messageTable.filterHam') },
            { value: '1', label: t('messageTable.filterSpam') },
          ]}
          value={String(spamFilterValue)}
          onChange={handleSpamFilterChange}
        />
        <Group gap="xs">
          <Button
            variant="default"
            size="compact-sm"
            disabled={records.length === 0}
            onClick={toggleExpandAll}
            leftSection={
              allExpanded ? <IconChevronsUp size={16} /> : <IconChevronsDown size={16} />
            }
          >
            {allExpanded ? t('messageTable.collapseAll') : t('messageTable.expandAll')}
          </Button>
          {props.headerActions}
        </Group>
      </Group>
      <DataTable
        minHeight={records.length === 0 ? 280 : undefined}
        emptyState={
          <EmptyState
            icon={IconInbox}
            title={t('messageTable.noMessagesFound')}
            description={t(
              'messageTable.emptyStateDescription',
              "Share your form to start collecting submissions. They'll show up here."
            )}
          />
        }
        withTableBorder
        borderRadius="md"
        shadow="xs"
        records={records}
        columns={columns}
        rowClassName={(record) => (record.is_spam ? classes.spamRow : '')}
        totalRecords={props.pagination.total}
        recordsPerPage={props.pagination.perPage}
        page={page.current}
        onPageChange={(p) => {
          page.current = p;
          loadRecords();
        }}
        fetching={isBusy}
        loadingText={t('messageTable.loading')}
        noRecordsText={t('messageTable.noMessagesFound')}
        rowExpansion={{
          allowMultiple: true,
          expanded: {
            recordIds: expandedRecordIds,
            onRecordIdsChange: handleExpandedRecordIdsChange,
          },
          content: ({ record }) => (
            <Box p="md" className={classes.expandedRow}>
              {expandedRowHtml(record, isBusy, toggleSpam, deleteCb)}
            </Box>
          ),
        }}
      />
    </Box>
  );
}
